import {
  Box,
  Loader,
  MultiSelect as MantineMultiSelect,
  type MultiSelectProps as MantineMultiSelectProps,
} from "@mantine/core";
import { IconAlertOctagon, IconChevronDown } from "@tabler/icons-react";
import clsx from "clsx";
import { useId } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Checkbox } from "@onengine/component-library";
import { emptyOptionData } from "./Select";
import styles from "./Select.module.scss";

export type SelectSize = "small" | "medium" | "large";

export type SelectOption = {
  value: string;
  label: string;
  renderLabel?: React.ReactNode;
  raw?: any;
  disabled?: boolean;
};

export type MultiSelectProps = Omit<
  MantineMultiSelectProps,
  "error" | "data" | "value" | "onChange"
> & {
  label?: string;
  optional?: boolean;
  required?: boolean;
  hint?: string;
  helpIcon?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  size?: SelectSize;
  error?: boolean | string;
  name: string;
  className?: string;
  /** Opt-in class for the outer wrapper, e.g. to drop the shared 300px min-width in a narrow grid cell. */
  wrapperClassName?: string;
  style?: React.CSSProperties;
  dataTestId?: string;
  options: SelectOption[] | undefined;
  loading?: boolean;
  showCheckboxes?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  isInfiniteScroll?: boolean;
  hasMoreData?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  onChangeCallback?: (value: string[]) => void;
};

const LOADER_OPTION_VALUE = "__multiselect_loader__";

export function MultiSelect({
  label,
  optional,
  required,
  hint,
  helpIcon,
  placeholder = "Select options",
  disabled,
  size = "small",
  error,
  name,
  className,
  wrapperClassName,
  style,
  dataTestId,
  options,
  loading,
  showCheckboxes = false,
  searchValue,
  onSearchChange,
  isInfiniteScroll = false,
  hasMoreData = false,
  isLoadingMore = false,
  onLoadMore,
  onChangeCallback,
  ...rest
}: MultiSelectProps) {
  const id = useId();
  const { control } = useFormContext();

  const baseData =
    options?.map((opt) => ({
      value: String(opt.value),
      label: opt.label,
      renderLabel: opt.renderLabel,
      raw: opt.raw,
      disabled: opt.disabled,
    })) ?? emptyOptionData;

  const shouldShowLoader = isInfiniteScroll && isLoadingMore;

  const data = shouldShowLoader
    ? [
        ...baseData,
        {
          value: LOADER_OPTION_VALUE,
          label: "Loading more...",
          disabled: true,
        },
      ]
    : baseData;

  const shouldHandleInfiniteScroll = isInfiniteScroll && typeof onLoadMore === "function";

  const { scrollAreaProps, ...mantineMultiSelectProps } = rest;

  const combinedScrollAreaProps = shouldHandleInfiniteScroll
    ? {
        ...scrollAreaProps,
        onBottomReached: () => {
          scrollAreaProps?.onBottomReached?.();
          if (hasMoreData && !isLoadingMore) {
            onLoadMore?.();
          }
        },
      }
    : scrollAreaProps;

  return (
    <Box className={clsx(styles.wrapper, styles[`wrapper-${size}`], wrapperClassName)}>
      {(label || optional) && (
        <Box className={styles.labelRow}>
          {label && (
            <label htmlFor={id} className={styles.labelText}>
              {label}
              {required && <span style={{ color: "var(--text-danger-default)" }}> *</span>}
              {helpIcon && <span className={styles.helpIcon}>{helpIcon}</span>}
            </label>
          )}
          {optional && <span className={styles.optionalText}>Optional</span>}
        </Box>
      )}

      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          let selectedValues: string[] = [];
          if (Array.isArray(field.value)) {
            selectedValues = field.value.map((val) => String(val));
          } else if (field.value !== null && field.value !== undefined && field.value !== "") {
            selectedValues = [String(field.value)];
          }

          return (
            <MantineMultiSelect
              {...field}
              value={selectedValues}
              onChange={(val) => {
                field?.onChange?.(val);
                onChangeCallback?.(val);
              }}
              data-testid={dataTestId}
              id={id}
              disabled={disabled}
              placeholder={placeholder}
              data={data}
              searchable
              searchValue={searchValue}
              onSearchChange={onSearchChange}
              clearable
              hidePickedOptions={!showCheckboxes}
              className={className}
              style={style}
              rightSection={
                loading ? (
                  <Loader size="xs" color="var(--text-weak)" />
                ) : (
                  <IconChevronDown size={16} />
                )
              }
              classNames={{
                input: clsx(
                  styles.select,
                  styles[`select-${size}`],
                  styles[`mulitSelect-${size}`],
                  error && styles.selectError,
                ),
                dropdown: styles.dropdown,
                option: clsx(styles.option, shouldShowLoader && styles.optionLoading),
                pill: styles.pill,
              }}
              renderOption={
                showCheckboxes
                  ? ({ option, checked }) => {
                      if (option.value === LOADER_OPTION_VALUE) {
                        return (
                          <div className={styles.loaderOption}>
                            <Loader
                              size="xs"
                              color="var(--text-weak)"
                              className={styles.loaderSpinner}
                            />
                            <span>Loading more...</span>
                          </div>
                        );
                      }
                      return (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            width: "100%",
                          }}
                        >
                          <div style={{ pointerEvents: "none" }}>
                            <Checkbox
                              state={checked ? "selected" : "deselected"}
                              size="small"
                              className={styles.checkbox}
                            />
                          </div>
                          <span>{(option as any).renderLabel || option.label}</span>
                        </div>
                      );
                    }
                  : ({ option }) => {
                      if (option.value === LOADER_OPTION_VALUE) {
                        return (
                          <div className={styles.loaderOption}>
                            <Loader
                              size="xs"
                              color="var(--text-weak)"
                              className={styles.loaderSpinner}
                            />
                            <span>Loading more...</span>
                          </div>
                        );
                      }
                      return (option as any).renderLabel || option.label;
                    }
              }
              scrollAreaProps={combinedScrollAreaProps}
              {...mantineMultiSelectProps}
            />
          );
        }}
      />

      {typeof error === "string" && (
        <Box className={styles.errorMessage}>
          <IconAlertOctagon size={16} className={styles.errorIcon} />
          <span>{error}</span>
        </Box>
      )}

      {hint && <Box className={styles.hint}>{hint}</Box>}
    </Box>
  );
}
